import React, { useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  TouchableHighlight,
  View,
} from "react-native";
import { COLORS } from "constants/Colors";
import { CustomInput } from "components/Inputs/CustomInput";
import { PrimaryText } from "components/Common/PrimaryText";
import { CURRENT_PASSWORD_ERROR_FIELD, changePassword } from "services/auth-service";
import { FullModal } from "./FullModal";

const MIN_PASSWORD_LENGTH = 8;

export function ChangePasswordModal({ setShowModal }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState(null);
  const [newPasswordError, setNewPasswordError] = useState(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState(null);
  const [generalError, setGeneralError] = useState(null);
  const [loading, setLoading] = useState(false);

  const changeCurrentPassword = (value) => {
    setCurrentPassword(value);
    setCurrentPasswordError(null);
    setGeneralError(null);
  };

  const changeNewPassword = (value) => {
    setNewPassword(value);
    setNewPasswordError(null);
    setGeneralError(null);
  };

  const changeConfirmPassword = (value) => {
    setConfirmPassword(value);
    setConfirmPasswordError(null);
    setGeneralError(null);
  };

  const validate = () => {
    let isValid = true;

    if (currentPassword === "") {
      setCurrentPasswordError("Current password is required");
      isValid = false;
    }

    if (newPassword === "") {
      setNewPasswordError("New password is required");
      isValid = false;
    } else if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setNewPasswordError(
        `New password must be at least ${MIN_PASSWORD_LENGTH} characters`
      );
      isValid = false;
    } else if (newPassword === currentPassword) {
      setNewPasswordError("New password must be different from your current password");
      isValid = false;
    }

    if (confirmPassword === "") {
      setConfirmPasswordError("Please confirm your new password");
      isValid = false;
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordError("Passwords do not match");
      isValid = false;
    }

    return isValid;
  };

  const closeModal = () => {
    if (loading) return;
    setShowModal(false);
  };

  const savePressed = async () => {
    setGeneralError(null);

    if (!validate()) return;

    setLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      window.alert("Password successfully changed");
      setShowModal(false);
    } catch (error) {
      if (error.field === CURRENT_PASSWORD_ERROR_FIELD) {
        setCurrentPasswordError(error.message);
      } else {
        setGeneralError(error.message);
      }
      setLoading(false);
    }
  };

  return (
    <FullModal setShowModal={closeModal} title="Change Password">
      <View style={styles.container}>
        <CustomInput
          title="Current Password"
          placeholder="Enter your current password"
          value={currentPassword}
          errorMessage={currentPasswordError}
          onChange={changeCurrentPassword}
          secure={true}
        />
        <CustomInput
          title="New Password"
          placeholder="Enter your new password"
          value={newPassword}
          errorMessage={newPasswordError}
          onChange={changeNewPassword}
          secure={true}
        />
        <CustomInput
          title="Confirm New Password"
          placeholder="Enter your new password again"
          value={confirmPassword}
          errorMessage={confirmPasswordError}
          onChange={changeConfirmPassword}
          secure={true}
        />
        {!!generalError && (
          <View style={styles.errorContainer}>
            <PrimaryText
              fontSize={14}
              fontWeight={400}
              style={styles.errorMessage}
            >
              {generalError}
            </PrimaryText>
          </View>
        )}
        <TouchableHighlight
          style={[styles.button, loading ? styles.inactive : styles.active]}
          underlayColor={"#DDD"}
          onPress={savePressed}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator size={"small"} color={COLORS.primary.white} />
          ) : (
            <PrimaryText fontSize={20} fontWeight={600} style={styles.buttonText}>
              Change Password
            </PrimaryText>
          )}
        </TouchableHighlight>
      </View>
    </FullModal>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 24,
  },
  errorContainer: {
    backgroundColor: COLORS.background.lightRed,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 12,
  },
  errorMessage: {
    color: COLORS.primary.error,
  },
  button: {
    marginTop: 12,
    height: 37,
    width: 235,
    borderRadius: 2,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
    backgroundColor: COLORS.primary.blue,
  },
  active: {
    opacity: 1,
  },
  inactive: {
    backgroundColor: COLORS.primary.lightGray,
  },
  buttonText: {
    color: COLORS.primary.white,
  },
});
